import type { Cache } from './cache.interface';

interface CacheRecord<T> {
  value: T;
  expiresAt: number | null;
}

/**
 * Default in-memory cache implementation — no external dependency required.
 * Per ADR guidance, a Redis-backed implementation of the same `Cache` interface
 * is a future, additive option, not needed at this phase's scale.
 */
export class MemoryCache implements Cache {
  private readonly store = new Map<string, CacheRecord<unknown>>();

  public get<T>(key: string): T | undefined {
    const record = this.store.get(key);
    if (!record) {
      return undefined;
    }
    if (record.expiresAt !== null && record.expiresAt < Date.now()) {
      this.store.delete(key);
      return undefined;
    }
    return record.value as T;
  }

  public set<T>(key: string, value: T, ttlMs?: number): void {
    this.store.set(key, {
      value,
      expiresAt: ttlMs ? Date.now() + ttlMs : null
    });
  }

  public invalidate(key: string): void {
    this.store.delete(key);
  }

  public clear(): void {
    this.store.clear();
  }
}
